// ProxyApi.js
// Efficient version with in-memory access-token caching and expiry check

import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { toast } from 'react-toastify';

// Use REACT_APP_API_URL if set, otherwise use relative URLs for dev proxy (set in package.json)
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const proxyapi = axios.create({
  baseURL: API_BASE_URL,
});

// In-memory cache for the access token and its expiry
let cachedToken = null;
let cachedTokenExpiry = null; // JS Date object

// Helper to check if token is still valid (with a 1-minute buffer)
function isTokenValid() {
  if (!cachedToken || !cachedTokenExpiry) return false;
  const now = Date.now();
  // Subtract 60-second buffer to avoid using a token that’s about to expire
  return now < cachedTokenExpiry - 60 * 1000;
}

// Helper to get a valid access token (from cache or fetch new)
async function getAccessToken() {
  if (isTokenValid()) {
    return cachedToken;
  }

  try {
    const session = await fetchAuthSession();
    const token = session.tokens.accessToken.toString();
    // Cognito tokens are JWTs; decode expiry from payload
    const payload = JSON.parse(atob(token.split('.')[1]));
    // ‘exp’ is in seconds since epoch
    cachedTokenExpiry = payload.exp * 1000;
    cachedToken = token;
    return token;
  } catch (error) {
    // Clear cache on error
    cachedToken = null;
    cachedTokenExpiry = null;
    throw error;
  }
}

// Add a request interceptor to include the token
proxyapi.interceptors.request.use(
  async (config) => {
    try {
      const token = await getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error adding auth token:', error);
      // Continue without the token – will get 401 responses
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Existing 429 interceptor
proxyapi.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 429) {
      const msg =
        error.response.data?.message ||
        'You are sending requests too quickly. Please wait and try again.';
      toast.error(msg);
    }
    return Promise.reject(error);
  }
);

export default proxyapi;

// Function to clear the cached token (allows manual cache reset)
export function clearTokenCache() {
  cachedToken = null;
  cachedTokenExpiry = null;
}
